import "./Beneficiary.css";
import * as lang from "./lang.json";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer } from "react";
import BeneficiaryInsuredForm from "./BeneficiaryInsuredForm";
import { BeneficiaryForm, BeneficiaryProps, CompanyData, getRadioBeneficiaryTypeValues, validateForm } from "./utils";
import { useAppDispatch, useAppSelector } from "../../../Store/hooks";
import { selectBeneficiaryTypes } from "../../../Store/BeneficiaryTypes/Selectors";
import { hideSpinner, showSpinner } from "../../../Store/Spinner/Actions";
import { getBeneficiaryTypesAsync } from "../../../Store/BeneficiaryTypes/Actions";
import { selectBeneficiaryFormData } from "../../../Store/Forms/Selectors";
import { selectEnvVariables } from "../../../Store/Variables/selectors";



/**
 * Main component.
 * @name Beneficiary
 * @param {BeneficiaryProps}
 * @returns {<Beneficiary />}
 */
const Beneficiary = ({
  insureds,
  onChangeFormData = (formData: any) => { },
}: BeneficiaryProps, ref: any) => {

  const dispatch = useAppDispatch();
  const beneficiaryTypes = useAppSelector(selectBeneficiaryTypes);
  const beneficiariesDataStoreForm = useAppSelector(selectBeneficiaryFormData);
  const variables = useAppSelector(selectEnvVariables) as any;

  const formReducer = (state: BeneficiaryForm, event: any) => {
    const newState = {
      ...state,
      [event.name]: event.value,
    };
    onChangeFormData(newState);
    return newState;
  };

  const getBeneficiaryInitialData = useMemo(() => {
    let data = JSON.parse(JSON.stringify(beneficiariesDataStoreForm));
    insureds.forEach((insured, index) => {
      if(!data.beneficiaryInsures[index]){
        data.beneficiaryInsures[index] = [{ name: '', lastname: '', dni: '', benefitPercentage: '100', beneficiaryRelationship: '', errorsMessages: [] }];
      }
    });
    return data;
  }, [beneficiariesDataStoreForm, insureds]);

  const [formData, setFormData] = useReducer(formReducer, getBeneficiaryInitialData);

  const handleChangeBeneficiaryInsures = (data: any, index: number) => {
    let beneficiaryInsures = [...formData.beneficiaryInsures];
    beneficiaryInsures[index] = data;
    setFormData({
      name: "beneficiaryInsures",
      value: beneficiaryInsures,
    });
  };

  const handleChangeIsLegalHeirsCheckbox = (isLegalHeirs: boolean, index: number) => {
    formData.isLegalHeirs[index] = isLegalHeirs;
    formData.beneficiaryInsures[index] = [{ name: '', lastname: '', dni: '', benefitPercentage: '100', beneficiaryRelationship: '', errorsMessages: [] }];
    setFormData(formData);
  };

  useEffect(() => {
    if (!beneficiaryTypes.length) {
      const getBeneficiaryTypes = async () => {
        await dispatch(showSpinner());
        await dispatch(getBeneficiaryTypesAsync());
        await dispatch(hideSpinner());
      }
      getBeneficiaryTypes();
    }
  }, []);

  useImperativeHandle(ref, () => ({
    validateForm: () => {

      let currentForm: BeneficiaryForm = validateForm(formData);
      setFormData({ ...currentForm });
      const hasBeneficiariesErrors = currentForm.beneficiaryInsures.filter(a => a.filter(b => b.errorsMessages.length > 0).length > 0).length > 0;
      const hasCompanyDataError = currentForm.companyData.errorsMessages.length > 0;
      if (hasBeneficiariesErrors || hasCompanyDataError) {
        return false;
      }

      return true;
    }
  }), [formData]);

  return (
    <div className="beneficiary-container">
      <div className="beneficiary-title" style={{ color: variables.REACT_APP_ACCENT_COLOR }}>
        <span>{lang.title}</span>
      </div>
      <div>
        {
          insureds.map((insured, index) => {
            return (
              <BeneficiaryInsuredForm
                onChange={handleChangeBeneficiaryInsures}
                onChangeIsLegalHeirsCheckbox={handleChangeIsLegalHeirsCheckbox}
                key={"BeneficiaryInsuredForm" + insured.index}
                indexRoot={insured.index}
                isLegalHeirs={formData.isLegalHeirs[index]}
                beneficiaries={formData.beneficiaryInsures[index]}
                name={insured.name}
                lastname={insured.lastname}
                dni={insured.documentNumber}
                errorsMessages={[...insured.errorsMessages]} //! TODO: No estamos utilizando la propiedad!
              />
            )
          })
        }
      </div>
    </div>
  );
};

export default forwardRef(Beneficiary);
